// DEPENDENCIES ---------------------------------------------------------------- //

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Moves } from '@gql/base-types';
import { Paper, Typography, Box, Button, Collapse, Avatar, IconButton, Tooltip } from '@mui/material';
import {
  BiTime,
  BiMailSend,
  BiPhone,
  BiCopyAlt,
  BiLinkExternal,
  BiChevronDown,
  BiChevronUp,
  BiUser,
  BiErrorCircle,
  BiPencil,
} from 'react-icons/bi';
import { Theme, useTheme } from '@mui/material';
import { toast } from 'react-toastify';
import { getLocationConfirmation } from '@features/moves/utils/getLocationConfirmation';
import FlowVisualization, { formatRelativeTime } from '@features/moves/components/FlowVisualization';
import { parseConfirmationFlow } from '@features/moves/utils/parseConfirmationFlow';
import {
  requiresPickupConfirmation,
  requiresDeliveryConfirmation,
} from '@features/moves/utils/checkConfirmationRequirement';
import { MoveLocationEditor } from '@hopdrive/feature-move-planner';

// TYPES ---------------------------------------------------------------- //

interface LocationConfirmationCardProps {
  move?: Moves;
  type: 'pickup' | 'delivery';
  historyExpanded?: boolean;
  onHistoryToggle?: () => void;
  /** Shows the pencil that changes this location (`canChangeMoveLocation`). */
  canEdit?: boolean;
  /** The rooftop whose locations are offered and whose lane the move moves to. */
  rooftopId?: number;
  /** Who a location change is attributed to. */
  updatedBy?: string;
}

// HELPER FUNCTIONS ---------------------------------------------------------------- //

/**
 * Formats JSON output as name-value pairs with connecting dots
 */
const formatOutputAsPairs = (output: string | object | undefined): Array<{ key: string; value: any }> => {
  if (!output) return [];

  let parsed: any;
  try {
    // If it's already a string, parse it; otherwise use as-is
    parsed = typeof output === 'string' ? JSON.parse(output) : output;
  } catch (e) {
    // If parsing fails, return empty array
    return [];
  }

  const pairs: Array<{ key: string; value: any }> = [];

  // Flatten nested objects recursively
  const flattenObject = (obj: any, prefix = '') => {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        const value = obj[key];

        if (value && typeof value === 'object' && !Array.isArray(value) && value.constructor === Object) {
          // Recursively flatten nested objects
          flattenObject(value, fullKey);
        } else {
          // Format the value for display
          let displayValue = value;
          if (value === null || value === undefined) {
            displayValue = '—';
          } else if (typeof value === 'boolean') {
            displayValue = value ? 'Yes' : 'No';
          } else if (Array.isArray(value)) {
            displayValue = value.join(', ');
          } else {
            displayValue = String(value);
          }
          pairs.push({ key: fullKey, value: displayValue });
        }
      }
    }
  };

  flattenObject(parsed);
  return pairs;
};

/**
 * Converts database status to display text
 * Displays the actual status without misrepresentation
 */
const getStatusDisplay = (status: string | null | undefined): string => {
  if (!status) return 'Pending';

  // Convert status to title case: "READY" -> "Ready", "NOT READY" -> "Not Ready"
  const normalized = status.replace(/_/g, ' ').toLowerCase();
  return normalized
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Returns background and text colors based on database status
 * Matches the pattern from status-badges-implementation.md
 */
const getStatusColor = (status: string | null | undefined) => {
  if (!status) {
    return { bgcolor: '#f4f6f8', color: '#666' }; // Gray (default)
  }

  // Normalize status for comparison
  const normalizedStatus = status.toLowerCase().replace(/_/g, ' ');

  switch (normalizedStatus) {
    case 'ready':
      return { bgcolor: '#e8f5e8', color: '#30b448' }; // Green
    case 'not ready':
      return { bgcolor: '#ffebee', color: '#ff2040' }; // Pink/Red
    case 'started':
      return { bgcolor: '#fff3e0', color: '#b8860b' }; // Orange/Amber
    case 'sent':
      return { bgcolor: '#e3f2fd', color: '#30a0e0' }; // Light Blue
    case 'pending':
      return { bgcolor: '#f4f6f8', color: '#666' }; // Gray
    case 'processing':
      return { bgcolor: '#f4f6f8', color: '#666' }; // Gray
    default:
      return { bgcolor: '#f4f6f8', color: '#666' }; // Gray (default)
  }
};

/**
 * Returns background color for status message area based on confirmation status
 * - Light green for "ready" (confirmed)
 * - Red for "not ready" (needs action)
 * - Yellow for other statuses that still need confirmation
 */
const getStatusMessageBackground = (status: string | null | undefined) => {
  if (!status) {
    return { backgroundColor: '#fffbf0', border: '1px solid #ffd54f' }; // Light yellow
  }

  const normalizedStatus = status.toLowerCase().replace(/_/g, ' ');

  switch (normalizedStatus) {
    case 'ready':
      return { backgroundColor: '#f1f8f4', border: '1px solid #c8e6c9' }; // Very light green
    case 'not ready':
      return { backgroundColor: '#ffebee', border: '1px solid #ffcdd2' }; // Light red/pink
    case 'sent':
    case 'started':
    case 'processing':
    case 'pending':
    case 'retry':
    default:
      return { backgroundColor: '#fffbf0', border: '1px solid #ffd54f' }; // Light yellow
  }
};

// COMPONENT ---------------------------------------------------------------- //

const LocationConfirmationCard = ({
  move,
  type,
  historyExpanded = false,
  onHistoryToggle,
  canEdit = false,
  rooftopId,
  updatedBy = 'dealer',
}: LocationConfirmationCardProps) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const cls = useStyles(theme);

  // Location editing state
  const [isEditingLocation, setIsEditingLocation] = useState(false);

  if (!move) return null;

  const locationData = getLocationConfirmation(move, type);
  if (!locationData) return null;

  const locationId = move.lane?.[type]?.id;

  // Check if confirmation is required for this location type
  const confirmationRequired =
    type === 'pickup' ? requiresPickupConfirmation(move) : requiresDeliveryConfirmation(move);

  const confirmation = locationData.confirmation;

  // Parse confirmation flow (must be called before any conditional returns)
  const flowSteps = useMemo(() => {
    return parseConfirmationFlow(locationData.confirmationset, type);
  }, [locationData.confirmationset, type]);

  // Calculate current attempt number (1-indexed)
  const currentAttempt = confirmation?.attempts?.length || 0;
  const totalAttempts = flowSteps.length || currentAttempt || 1;
  const lastSentTime = confirmation?.attempts?.[0]?.triggerTime;

  const getStatusDescription = (status: string, latestAttempt: any) => {
    const normalizedStatus = status?.toLowerCase() || 'pending';

    // Check if "sent" is expired (should show as retry)
    const isExpired =
      normalizedStatus === 'sent' &&
      latestAttempt?.expirationTime &&
      new Date(latestAttempt.expirationTime) < new Date();
    const displayStatus = isExpired ? 'retry' : normalizedStatus;

    switch (displayStatus) {
      case 'pending':
        return 'Waiting to send confirmation';
      case 'sent':
        return `Confirmation sent ${latestAttempt?.timeAgo || ''}`;
      case 'retry':
        return `Marked for retry • ${latestAttempt?.timeAgo || ''}`;
      case 'processing':
        return 'Contact opened link • awaiting submission';
      case 'started':
        return 'Contact opened link • awaiting submission';
      case 'ready':
        return 'Confirmation received';
      case 'not ready':
        // If escalated, indicate that vehicle is not ready and action is needed
        if (latestAttempt?.escalated) {
          return 'Vehicle not ready • action required';
        }
        return 'No response • awaiting next steps';
      default:
        return 'Unknown status';
    }
  };

  const handleCopyLink = async () => {
    if (!confirmation?.magicUrl) return;
    try {
      const url = new URL(confirmation.magicUrl);
      url.searchParams.set('src', 'portal-copy-link');
      await navigator.clipboard.writeText(url.toString());
      toast.success(`${type === 'pickup' ? 'Pickup' : 'Delivery'} confirmation link copied!`);
    } catch (err) {
      console.error('Failed to copy link:', err);
      toast.error('Failed to copy link');
    }
  };

  const handleOpenLink = () => {
    if (!confirmation?.magicUrl) return;
    try {
      const url = new URL(confirmation.magicUrl);
      url.searchParams.set('src', 'portal-open-link');
      window.open(url.toString(), '_blank');
    } catch (err) {
      console.error('Failed to open link:', err);
      // Fallback to opening the original URL if URL parsing fails
      window.open(confirmation.magicUrl, '_blank');
    }
  };

  const latestAttempt = confirmation?.attempts?.[0];

  // Get escalated flag from latest attempt (already computed correctly in getLocationConfirmation)
  const isEscalated = latestAttempt?.escalated ?? false;
  // Use database status directly - no mapping needed
  const status = latestAttempt?.status ?? locationData.relevantRequests?.[0]?.status ?? null;

  // Get status display text and colors based on actual database status
  // Escalation is shown separately via the escalated badge, so we don't change the status display
  const statusDisplay = getStatusDisplay(status);
  const statusColors = getStatusColor(status);

  // Determine if we should show badges (only if confirmation is required for this location)
  const shouldShowBadges = confirmationRequired && (confirmation || locationData.confirmationset);

  return (
    <Paper
      variant='custom'
      sx={{
        width: '100%',
        borderRadius: '8px',
        overflow: 'visible', // Changed from 'hidden' to allow badges to overflow
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        position: 'relative',
        border: isEscalated ? '2px solid' : '1px solid',
        borderColor: isEscalated ? '#ff2040' : theme.palette.divider,
      }}
    >
      {/* Status Badges - Positioned at top center, half on/half off the card */}
      {shouldShowBadges && (
        <Box
          sx={{
            position: 'absolute',
            top: -16, // Half on/half off the card
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {/* Main Status Badge */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              px: 2,
              py: 1,
              borderRadius: 2,
              fontSize: '14px',
              fontWeight: 700,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              border: '2px solid white',
              ...statusColors,
            }}
          >
            {statusDisplay}
          </Box>

          {/* Escalated Badge (shown when escalated = true) */}
          {isEscalated && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.25,
                py: 1,
                bgcolor: '#ff2040',
                color: 'white',
                borderRadius: 2,
                fontSize: '11px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
                boxShadow: '0 2px 8px rgba(255, 32, 64, 0.3)',
                border: '2px solid white',
              }}
            >
              <BiErrorCircle size={14} />
              Escalated
            </Box>
          )}
        </Box>
      )}

      {/* Content - Add padding top to account for overlapping badge */}
      <Box sx={{ p: 2, pt: shouldShowBadges ? 2 : 2, display: 'flex', flexDirection: 'column', flex: 1 }}>
        {/* Location Info */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant='h6' sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                {type} Location
              </Typography>
              {canEdit && !isEditingLocation && (
                <Tooltip title={`Edit ${type} location`} placement='top'>
                  <IconButton
                    size='small'
                    onClick={() => setIsEditingLocation(true)}
                    sx={{
                      ml: 0.5,
                      color: theme.palette.text.secondary,
                      '&:hover': {
                        color: theme.palette.primary.main,
                      },
                    }}
                  >
                    <BiPencil size={18} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {locationData.region && !isEditingLocation && (
              <Typography variant='body2' sx={{ color: theme.palette.text.secondary }}>
                {locationData.region}
              </Typography>
            )}
          </Box>

          {/* Editing mode: Show location select */}
          {isEditingLocation ? (
            <MoveLocationEditor
              move={move}
              role={type}
              rooftopId={rooftopId}
              updatedBy={updatedBy}
              onClose={() => setIsEditingLocation(false)}
            />
          ) : (
            /* Read-only mode: Show location details */
            <Tooltip placement='top' title='Click to see details for this location'>
              <Box
                onClick={() => {
                  if (locationId) {
                    navigate(`/locations/${locationId}`);
                  }
                }}
                sx={{
                  p: 0.75,
                  borderRadius: '6px',
                  backgroundColor: theme.palette.action.hover,
                  cursor: 'pointer',
                  transition: '0.1s ease-in-out',
                  mb: 0.5,
                  '&:hover': {
                    backgroundColor: theme.palette.action.selected,
                  },
                }}
              >
                <Typography sx={{ fontWeight: 500, mb: 0.5 }}>{locationData.name}</Typography>
                <Typography variant='body2' sx={{ color: theme.palette.text.secondary }}>
                  {locationData.address}
                </Typography>
              </Box>
            </Tooltip>
          )}
        </Box>

        {/* Contact Card */}
        {locationData.contact && (
          <Box
            sx={{
              backgroundColor: theme.palette.grey[50],
              borderRadius: '8px',
              p: 1.5,
              mt: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            {/* Left: Contact Info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: theme.palette.grey[300],
                  color: theme.palette.text.secondary,
                }}
              >
                <BiUser size={20} />
              </Avatar>
              <Box>
                <Typography sx={{ fontWeight: 500 }}>{locationData.contact.name}</Typography>
                {locationData.contact.email && (
                  <Box
                    component='a'
                    href={`mailto:${locationData.contact.email}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mt: 0.25,
                      textDecoration: 'none',
                      color: 'inherit',
                      '&:hover': {
                        color: theme.palette.primary.main,
                      },
                    }}
                  >
                    <BiMailSend size={14} color={theme.palette.text.secondary} />
                    <Typography variant='caption' sx={{ color: theme.palette.text.secondary }}>
                      {locationData.contact.email}
                    </Typography>
                  </Box>
                )}
                {locationData.contact.phone && (
                  <Box
                    component='a'
                    href={`tel:${locationData.contact.phone}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mt: 0.25,
                      textDecoration: 'none',
                      color: 'inherit',
                      '&:hover': {
                        color: theme.palette.primary.main,
                      },
                    }}
                  >
                    <BiPhone size={14} color={theme.palette.text.secondary} />
                    <Typography variant='caption' sx={{ color: theme.palette.text.secondary }}>
                      {locationData.contact.phone}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Center: Flow Visualization - Only show if confirmation is required */}
            {confirmationRequired && confirmation && (
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
                <FlowVisualization
                  flowSteps={flowSteps}
                  currentAttempt={currentAttempt}
                  totalAttempts={totalAttempts}
                  lastSentTime={lastSentTime}
                />
              </Box>
            )}

            {/* Right: Time since last sent - Only show if confirmation is required */}
            {confirmationRequired && confirmation && lastSentTime && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  flexShrink: 0,
                  fontSize: '13px',
                  color: 'text.secondary',
                }}
              >
                <BiTime size={16} />
                <Typography sx={{ fontSize: '13px', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  {formatRelativeTime(lastSentTime)}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Confirmation Status Details - Only show if confirmation is required */}
        {confirmationRequired && confirmation && (
          <>
            <Box
              sx={{
                borderTop: `1px solid ${theme.palette.divider}`,
                pt: 2,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    borderRadius: '6px',
                    ...getStatusMessageBackground(status),
                  }}
                >
                  <BiTime size={16} color={theme.palette.text.secondary} />
                  <Typography variant='body2' sx={{ color: theme.palette.text.secondary }}>
                    {getStatusDescription(confirmation.status, latestAttempt)}
                  </Typography>
                </Box>

                {/* Magic Link Actions */}
                {confirmationRequired && confirmation.status !== 'ready' && confirmation.magicUrl && (
                  <Tooltip title='Use this link to manually promote confirmation'>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.5,
                        py: 0.75,
                        borderRadius: '20px',
                        border: `1px solid #ffc107`,
                        backgroundColor: '#ffc107',
                        boxShadow: '0 2px 4px rgba(255, 193, 7, 0.3)',
                        cursor: 'pointer',
                      }}
                    >
                      <Typography variant='caption' sx={{ color: '#202430', fontSize: '12px', fontWeight: 600 }}>
                        Confirm
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Tooltip title='Copy Link'>
                          <IconButton
                            size='small'
                            onClick={handleCopyLink}
                            sx={{
                              color: '#202430',
                              padding: '2px',
                              '&:hover': {
                                color: '#202430',
                                backgroundColor: 'rgba(32, 36, 48, 0.1)',
                              },
                            }}
                          >
                            <BiCopyAlt size={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Open Link'>
                          <IconButton
                            size='small'
                            onClick={handleOpenLink}
                            sx={{
                              color: '#202430',
                              padding: '2px',
                              '&:hover': {
                                color: '#202430',
                                backgroundColor: 'rgba(32, 36, 48, 0.1)',
                              },
                            }}
                          >
                            <BiLinkExternal size={16} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Tooltip>
                )}
              </Box>

              {/* Expandable History - Top aligned for consistent positioning */}
              {confirmation.attempts.length > 0 && (
                <Box sx={{ mt: 1, pt: 1, ml: -0.5, pb: 0 }}>
                  <Button
                    variant='text'
                    size='small'
                    startIcon={historyExpanded ? <BiChevronUp /> : <BiChevronDown />}
                    onClick={onHistoryToggle}
                    sx={{
                      mb: historyExpanded ? 0.5 : 0,
                      color: theme.palette.text.secondary,
                      fontWeight: 400,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        color: theme.palette.text.secondary,
                      },
                    }}
                  >
                    History ({confirmation.attempts.length} attempt{confirmation.attempts.length > 1 ? 's' : ''})
                  </Button>
                  <Collapse in={historyExpanded}>
                    <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {confirmation.attempts.map(attempt => (
                        <Box
                          key={attempt.id}
                          sx={{
                            pt: 1.5,
                            px: 1.5,
                            pb: 1,
                            borderRadius: '8px',
                            backgroundColor: theme.palette.grey[50],
                            border: `1px solid ${theme.palette.divider}`,
                          }}
                        >
                          <Box
                            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}
                          >
                            <Typography variant='body2' sx={{ fontWeight: 500 }}>
                              Attempt #{attempt.attemptNumber}
                            </Typography>
                            {/* Display raw database status directly - no mapping needed for individual attempts */}
                            {attempt.status && (
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: '16px', // Pill shape
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  border: '1px solid',
                                  ...getStatusColor(attempt.status),
                                  borderColor: getStatusColor(attempt.status).color,
                                  bgcolor: 'transparent',
                                }}
                              >
                                {getStatusDisplay(attempt.status)}
                              </Box>
                            )}
                          </Box>
                          <Typography variant='caption' sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                            Sent {attempt.sentAt} • {attempt.timeAgo}
                          </Typography>
                          {(() => {
                            const outputPairs = attempt.output ? formatOutputAsPairs(attempt.output) : [];
                            return outputPairs.length > 0 ? (
                              <Box
                                sx={{
                                  p: 1.5,
                                  backgroundColor: theme.palette.background.paper,
                                  borderRadius: '4px',
                                  border: `1px solid ${theme.palette.divider}`,
                                  fontSize: '0.75rem',
                                  mb: 1,
                                  width: '100%',
                                  maxWidth: '100%',
                                  overflow: 'hidden',
                                  boxSizing: 'border-box',
                                }}
                              >
                                {outputPairs.map((pair, index) => (
                                  <Box
                                    key={index}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'baseline',
                                      mb: index < outputPairs.length - 1 ? 0.75 : 0,
                                      minWidth: 0,
                                      width: '100%',
                                    }}
                                  >
                                    <Typography
                                      variant='caption'
                                      component='span'
                                      sx={{
                                        flexShrink: 0,
                                        color: theme.palette.text.secondary,
                                        fontWeight: 500,
                                        mr: 1,
                                        textTransform: 'capitalize',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {pair.key.replace(/_/g, ' ')}
                                    </Typography>
                                    <Box
                                      sx={{
                                        flex: '1 1 auto',
                                        minWidth: 8,
                                        height: 1,
                                        borderBottom: `1px dotted ${theme.palette.divider}`,
                                        mx: 0.5,
                                        alignSelf: 'flex-end',
                                      }}
                                    />
                                    <Typography
                                      variant='caption'
                                      component='span'
                                      sx={{
                                        flexShrink: 0,
                                        color: theme.palette.text.primary,
                                        fontWeight: 400,
                                        ml: 1,
                                        textAlign: 'right',
                                        wordBreak: 'break-word',
                                        maxWidth: { xs: '50%', sm: '60%' },
                                      }}
                                    >
                                      {pair.value}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            ) : null;
                          })()}
                        </Box>
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </Paper>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme: Theme) => ({});

// EXPORT ---------------------------------------------------------------- //

export default LocationConfirmationCard;
